import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Box, Grid, Paper, Typography, IconButton, CircularProgress } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTheme } from "@mui/material/styles";
import { getTextColor } from '../../../../utils/colorUtils';
import { ModalStackProvider, useModalStack } from '../../../../components/Modal/ModalStackContext';
import MainCard from "../../../../components/cards/MainCard";

import ContentPackCoursePreviewModal from '../../../content-packs/ContentPackCoursePreviewModal';
import { CalendarSimulationPreviewModal } from '../modals/CalendarSimulationPreview';
import SimulationCollectionPreview from '../../../sim-collections/SimulationCollectionPreview';
import ExportToPresetModal from '../modals/ExportToPresetModal';
import AddItemModal from '../modals/AddItemModal';
import EditItemModal from '../modals/EditItemModal';
import MultiItemSelectModal from '../modals/MultiItemSelectModal';
import CalendarModalRenderer from '../modals/CalendarModalRenderer';
import RecipientSelectionModal from '../modals/RecipientSelectionModal';

import useCalendarData from '../../hooks/useCalendarData';
import useCalendarNavigation from '../../hooks/useCalendarNavigation';

import CalendarGrid from './CalendarGrid';
import CalendarHeader from './CalendarHeader';
import PaginationControls from './PaginationControls';

import { useGetSimulationsQuery } from "../../../../store/services/simulation-service";
import { useGetSimulationCollectionsQuery } from "../../../../store/services/simulation-service";
import { useGetCoursesQuery, useGetContentPacksQuery } from "../../../../store/services/university-service";
import { useGetFeatureSwitchesQuery } from "../../../../store/services/tenancy.service";
import { formatDate } from '../../utils/dateUtils';

const ContentDeliveryCalendarWithProvider = (props) => {
    return (
        <ModalStackProvider>
            <ContentDeliveryCalendar {...props} />
        </ModalStackProvider>
    );
};

const ContentDeliveryCalendar = ({
    mode = 'edit',
    deliveryGroupId,
    onClose,
    showNotification,
    presetData,
    startDate
}) => {
    const theme = useTheme();
    const { pushModal, popModal } = useModalStack();
    const { tenancyId } = useSelector((state) => state.tenancy);

    // State for recipient edit modal
    const [editRecipientModalOpen, setEditRecipientModalOpen] = useState(false);

    const contentTypeColors = useMemo(() => ({
        'Simulation': theme.palette.primary.main,
        'Course': theme.palette.warning.main,
        'RandomSimulation': theme.palette.primary.main,
        'ContentPack': theme.palette.warning.main,
    }), [theme.palette.primary.main, theme.palette.warning.main]);

    // Use custom hooks
    const calendarData = useCalendarData(mode, deliveryGroupId, presetData, startDate, showNotification);
    const navigation = useCalendarNavigation(calendarData.monthsToDisplay);

    // RTK Query hooks for data
    const { data: simData } = useGetSimulationsQuery({
        perPage: 1000,
        page: 0,
        includes: "",
        query: "",
        additionalQueryParams: `&published=true&tenancyId=${tenancyId}`
    });

    const { data: courseData } = useGetCoursesQuery({
        perPage: 1000,
        page: 0,
        includes: "",
        query: "",
        additionalQueryParams: `&published=true&tenancyId=${tenancyId}`
    });

    const { data: collectionData } = useGetSimulationCollectionsQuery({
        perPage: 1000,
        page: 0,
        searchTerm: "",
        tenancyId: tenancyId,
        published: true
    });

    const { data: contentPackData } = useGetContentPacksQuery({
        perPage: 1000,
        page: 0,
        searchTerm: "",
        tenancyId: tenancyId
    });

    // Get feature switches for current tenancy
    const {
        data: featureSwitchesApiResponse,
        isLoading: featureSwitchesLoading
    } = useGetFeatureSwitchesQuery({ tenancyId });

    // Extract actual feature data
    const featureSwitchesApiData = featureSwitchesApiResponse?.data || featureSwitchesApiResponse;

    // Handler for opening recipient edit modal
    const handleEditRecipient = useCallback(() => {
        setEditRecipientModalOpen(true);
    }, []);

    // Handler for confirming recipient change
    const handleRecipientConfirm = useCallback((newRecipientData) => {
        calendarData.handleRecipientChange(newRecipientData);
        setEditRecipientModalOpen(false);
    }, [calendarData]);

    const handleDayClickWithModal = useCallback((date, itemsAtDate = []) => {
        // Call the existing handler first
        calendarData.handleDayClick(date, itemsAtDate);

        // Now check what modal should open based on the result
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const cellDate = new Date(date);
        cellDate.setHours(0, 0, 0, 0);
        const isPastOrToday = cellDate <= today;

        if (isPastOrToday) {
            if (itemsAtDate.length >= 1) {
                pushModal({
                    type: 'multiSelect',
                    props: {
                        items: itemsAtDate,
                        date: date,
                        readOnly: true
                    }
                });
            }
        } else {
            if (calendarData.isMoveMode && calendarData.itemToMove) {
                // Move mode - no modal needed
                return;
            } else if (itemsAtDate.length >= 1) {
                pushModal({
                    type: 'multiSelect',
                    props: {
                        items: itemsAtDate,
                        date: date,
                        readOnly: false
                    }
                });
            } else {
                // Open add modal
                pushModal({
                    type: 'addItem',
                    props: { date }
                });
            }
        }
    }, [calendarData, pushModal]);

    const handleAddNewItemFromMultiSelect = useCallback((date) => {
        // Reset form state with the correct date before opening add modal
        calendarData.resetFormStateForNewItem(date);
        popModal(); // Close multi-select
        pushModal({
            type: 'addItem',
            props: { date }
        });
    }, [calendarData, popModal, pushModal]);

    const handleMultiSelectItemClick = useCallback((item) => {
        calendarData.handleItemClick(item);

        popModal(); // Close multi-select

        // Small delay to prevent modal flicker
        setTimeout(() => {
            pushModal({
                type: 'editItem',
                props: { item }
            });
        }, 50);
    }, [popModal, pushModal, calendarData]);

    const handleExportToPresetWithModal = useCallback(() => {
        if (calendarData.calendarItems.length === 0) {
            showNotification('Cannot create a preset with no items', 'error');
            return;
        }
        pushModal({
            type: 'exportPreset',
            props: {}
        });
    }, [calendarData.calendarItems, showNotification, pushModal]);

    const handlePreview = useCallback(() => {
        if (!calendarData.formState.contentId) return;

        if (calendarData.formState.contentType === 'Course') {
            // Check if this is a Content Pack (subtype of Course)
            if (calendarData.formState.isContentPack || calendarData.formState.selectedContentName === 'Content Pack') {
                // Handle ContentPack preview
                const contentPack = contentPackData?.find(pack => pack.internalId === calendarData.formState.selectedContentPackId);
                if (contentPack) {
                    pushModal({
                        type: 'contentPackPreview',
                        props: { contentPackId: contentPack.internalId }
                    });
                }
            } else {
                // Handle individual Course preview
                const course = courseData?.find(course => course.courseName === calendarData.formState.contentId);
                if (course) {
                    pushModal({
                        type: 'coursePreview',
                        props: { courseId: course.id }
                    });
                }
            }
        } else if (calendarData.formState.contentType === 'Simulation') {
            if (calendarData.formState.selectedContentName === 'Random Simulation') {
                const collection = collectionData?.find(col => col.internalId === calendarData.formState.selectedCollectionId);
                if (collection) {
                    pushModal({
                        type: 'collectionPreview',
                        props: { collectionId: collection.internalId }
                    });
                }
            } else {
                const simulation = simData?.find(sim => sim.simulationName === calendarData.formState.contentId);
                if (simulation) {
                    pushModal({
                        type: 'simulationPreview',
                        props: { simulationId: simulation.id }
                    });
                }
            }
        }
    }, [calendarData.formState, courseData, simData, collectionData, contentPackData, pushModal]);

    const handleSaveChanges = useCallback(async () => {
        const success = await calendarData.handleSaveChanges();
        if (success) {
            onClose(true);
        }
    }, [calendarData, onClose]);

    if (calendarData.isLoadingRequest && mode === 'edit') {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4 }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    Loading calendar data...
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <MainCard
                title={
                    <CalendarHeader
                        mode={mode}
                        isMoveMode={calendarData.isMoveMode}
                        deliveryGroupName={calendarData.deliveryGroupName}
                        setDeliveryGroupName={calendarData.setDeliveryGroupName}
                        unsavedChanges={calendarData.unsavedChanges}
                        isUpdating={calendarData.isUpdating}
                        isCreating={calendarData.isCreating}
                        isAdmin={calendarData.isAdmin}
                        calendarItemsCount={calendarData.calendarItems.length}
                        attemptedSave={calendarData.attemptedSave}
                        onSave={handleSaveChanges}
                        onExportToPreset={handleExportToPresetWithModal}
                        onCancelMove={calendarData.handleCancelMoveMode}
                        onClose={onClose}
                        recipientData={calendarData.recipientData}
                        onEditRecipient={handleEditRecipient}
                        isLoadingRecipientName={calendarData.isLoadingRecipientName}
                    />
                }
                content={true}
                sx={{ bgcolor: 'background.default' }}
            >
                {/* Banner for preset mode */}
                {mode === 'preset' && calendarData.showBanner && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            mb: 3,
                            bgcolor: theme.palette.primary.main,
                            color: getTextColor(theme.palette.primary.main),
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderRadius: '8px'
                        }}
                    >
                        <Box>
                            <Typography variant="h6">
                                Template Schedule Starting {new Date(startDate).toLocaleDateString()}
                            </Typography>
                            <Typography variant="body2">
                                Items from the template "{presetData?.name}" have been placed on your calendar.
                                You can add, remove, or move items before creating the final schedule.
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            sx={{ color: 'inherit' }}
                            onClick={() => calendarData.setShowBanner(false)}
                        >
                            <CancelIcon />
                        </IconButton>
                    </Paper>
                )}

                {/* Banner for create mode with no items */}
                {mode === 'create' && calendarData.calendarItems.length === 0 && calendarData.showBanner && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            mb: 3,
                            bgcolor: theme.palette.primary.main,
                            color: getTextColor(theme.palette.primary.main),
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderRadius: '8px'
                        }}
                    >
                        <Box>
                            <Typography variant="h6">
                                Click on any day to add content
                            </Typography>
                            <Typography variant="body2">
                                To create a journey, add at least one content item to the calendar
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            sx={{ color: 'inherit' }}
                            onClick={() => calendarData.setShowBanner(false)}
                        >
                            <CancelIcon />
                        </IconButton>
                    </Paper>
                )}

                {/* Calendar Grid */}
                <Grid container>
                    <CalendarGrid
                        monthsByYear={navigation.monthsByYear}
                        itemsByDate={calendarData.itemsByDate}
                        batchRanges={calendarData.batchRanges}
                        onDayClick={handleDayClickWithModal}
                        isMoveMode={calendarData.isMoveMode}
                        contentTypeColors={contentTypeColors}
                    />
                </Grid>

                {/* Pagination Controls */}
                <PaginationControls
                    currentPage={navigation.currentPage}
                    totalPages={navigation.totalPages}
                    handlePreviousPage={navigation.handlePreviousPage}
                    handleNextPage={navigation.handleNextPage}
                    isMoveMode={calendarData.isMoveMode}
                />

                {/* Calendar Modal Renderer */}
                <CalendarModalRenderer
                    formState={calendarData.formState}
                    handleFormChange={calendarData.handleFormChange}
                    handleContentTypeChange={calendarData.handleContentTypeChange}
                    handleContentSelection={calendarData.handleContentSelection}
                    handleCollectionSelection={calendarData.handleCollectionSelection}
                    handleContentPackSelection={calendarData.handleContentPackSelection}
                    handleBatchSendChange={calendarData.handleBatchSendChange}
                    onAddNewItemFromMultiSelect={handleAddNewItemFromMultiSelect}
                    onMultiSelectItemClick={handleMultiSelectItemClick}
                    simulationData={simData}
                    courseData={courseData}
                    collectionData={collectionData}
                    contentPackData={contentPackData}
                    onAdd={calendarData.handleAddItem}
                    onUpdate={calendarData.handleUpdateItem}
                    onDelete={calendarData.handleDeleteItem}
                    onEnterMoveMode={calendarData.handleEnterMoveMode}
                    onPreview={handlePreview}
                    contentTypeColors={contentTypeColors}
                    calendarItems={calendarData.calendarItems}
                    showNotification={showNotification}
                    featureSwitchesApiData={featureSwitchesApiData}
                />
            </MainCard>

            {/* Recipient Selection Modal for editing */}
            <RecipientSelectionModal
                open={editRecipientModalOpen}
                onClose={() => setEditRecipientModalOpen(false)}
                onConfirm={handleRecipientConfirm}
                initialRecipientType={calendarData.recipientData?.recipientType}
                initialRecipientId={calendarData.recipientData?.sendToId}
                initialRecipientName={calendarData.recipientData?.displayName}
            />
        </Box>
    );
};

export default ContentDeliveryCalendarWithProvider;